import React, { useState, useEffect } from "react";
import {
  Home,
  Users,
  Calendar,
  DollarSign,
  Shield,
  MapPin,
  Clock,
  Building2,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Edit3,
  Save,
  X,
} from "lucide-react";
import { realEstateCasesApi, trustApi } from "../../services/api";
import { motion } from "framer-motion";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Props {
  matterId: string;
  reCase: any;
  onUpdate: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const PIPELINE_STAGES = [
  { key: "intake", label: "Intake" },
  { key: "due_diligence", label: "Due Diligence" },
  { key: "requisitions", label: "Requisitions" },
  { key: "signing", label: "Signing" },
  { key: "pre_closing", label: "Pre-Closing" },
  { key: "closing_day", label: "Closing Day" },
  { key: "post_closing", label: "Post-Closing" },
  { key: "reporting_archive", label: "Archive" },
];

const ROLE_COLORS: Record<string, string> = {
  purchaser: "bg-cyan-100 text-cyan-700",
  vendor: "bg-amber-100 text-amber-700",
  borrower: "bg-blue-100 text-blue-700",
  lender: "bg-purple-100 text-purple-700",
  guarantor: "bg-slate-100 text-slate-600",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  cancelled: "bg-red-100 text-red-700",
};

const KYC_COLORS: Record<string, { bg: string; icon: React.ReactNode; label: string }> = {
  not_started: { bg: "bg-red-100 text-red-700", icon: <XCircle className="h-3.5 w-3.5" />, label: "Not Started" },
  in_progress: { bg: "bg-amber-100 text-amber-700", icon: <AlertTriangle className="h-3.5 w-3.5" />, label: "In Progress" },
  complete: { bg: "bg-emerald-100 text-emerald-700", icon: <CheckCircle2 className="h-3.5 w-3.5" />, label: "Complete" },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatLabel = (s: string) =>
  s?.split("_").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

const formatCAD = (cents: number | null) =>
  cents != null
    ? new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100)
    : " - ";

const formatDate = (d: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
    : "Not set";

const daysFromNow = (d: string | null) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
  return diff;
};

// ─── Component ──────────────────────────────────────────────────────────────

const RealEstateOverviewTab: React.FC<Props> = ({ matterId, reCase, onUpdate }) => {
  const [trustBalance, setTrustBalance] = useState<number | null>(null);
  const [showFullLegal, setShowFullLegal] = useState(false);

  useEffect(() => {
    trustApi
      .getSubledgerSummary({ matterId })
      .then((res) => setTrustBalance(res.data?.balanceCents ?? null))
      .catch(() => {});
  }, [matterId]);

  if (!reCase) return null;

  const property = reCase.property || {};
  const parties = reCase.parties || {};
  const dates = reCase.dates || {};
  const financial = reCase.financial || {};
  const compliance = reCase.compliance || {};
  const soa = reCase.soa || {};
  const ltt = reCase.ltt || {};

  const currentStageIdx = PIPELINE_STAGES.findIndex((s) => s.key === reCase.pipelineStage);
  const closingDays = daysFromNow(dates.closingDate);

  // ─── Card wrapper ───────────────────────────────────────────────────────

  const Card: React.FC<{
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
    className?: string;
  }> = ({ icon, title, children, className = "" }) => (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl shadow-sm border border-slate-200 p-5 ${className}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-cyan-50 flex items-center justify-center">
          {icon}
        </div>
        <h3 className="font-semibold text-slate-800">{title}</h3>
      </div>
      {children}
    </motion.div>
  );

  const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right">{value || <span className="text-slate-400">--</span>}</span>
    </div>
  );

  // ─── Timeline date item ─────────────────────────────────────────────────

  const TimelineDate: React.FC<{ label: string; date: string | null }> = ({ label, date }) => {
    const days = daysFromNow(date);
    const isPast = days !== null && days < 0;
    const isToday = days === 0;

    return (
      <div className="flex flex-col items-center flex-1 min-w-0">
        <div
          className={`h-8 w-8 rounded-full flex items-center justify-center mb-1.5 ${
            isPast
              ? "bg-emerald-100"
              : isToday
              ? "bg-blue-100 ring-2 ring-blue-300 animate-pulse"
              : "bg-slate-100"
          }`}
        >
          {isPast ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : isToday ? (
            <Clock className="h-4 w-4 text-blue-600" />
          ) : (
            <Calendar className="h-4 w-4 text-slate-400" />
          )}
        </div>
        <span className="text-xs font-medium text-slate-700 text-center">{label}</span>
        <span className={`text-xs mt-0.5 ${date ? "text-slate-500" : "text-slate-400 italic"}`}>
          {formatDate(date)}
        </span>
        {days !== null && (
          <span className={`text-[11px] mt-0.5 font-medium ${isPast ? "text-emerald-600" : isToday ? "text-blue-600" : "text-slate-500"}`}>
            {isToday ? "Today" : isPast ? `${Math.abs(days)}d ago` : `In ${days}d`}
          </span>
        )}
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* ── 1. Matter Summary (full width) ─────────────────────────────────── */}
      <Card
        icon={<Home className="h-4 w-4 text-cyan-600" />}
        title="Matter Summary"
        className="lg:col-span-2"
      >
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {reCase.transactionType && (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">
              {formatLabel(reCase.transactionType)}
            </span>
          )}
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[reCase.status] || "bg-slate-100 text-slate-600"}`}>
            {formatLabel(reCase.status || "active")}
          </span>
          {closingDays !== null && (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${closingDays < 0 ? "bg-red-100 text-red-700" : closingDays <= 7 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
              {closingDays < 0 ? `${Math.abs(closingDays)}d past closing` : closingDays === 0 ? "Closing today" : `${closingDays}d to closing`}
            </span>
          )}
        </div>

        {/* Pipeline stages */}
        <div className="flex items-center gap-0 overflow-x-auto pb-1">
          {PIPELINE_STAGES.map((stage, idx) => {
            const isComplete = idx < currentStageIdx;
            const isCurrent = idx === currentStageIdx;
            return (
              <React.Fragment key={stage.key}>
                {idx > 0 && (
                  <div className={`flex-shrink-0 h-0.5 w-4 sm:w-6 ${isComplete ? "bg-emerald-400" : "bg-slate-200"}`} />
                )}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      isComplete
                        ? "bg-emerald-500 text-white"
                        : isCurrent
                        ? "bg-cyan-500 text-white ring-2 ring-cyan-200"
                        : "bg-slate-100 text-slate-400 border border-slate-200"
                    }`}
                  >
                    {isComplete ? <CheckCircle2 className="h-3.5 w-3.5" /> : idx + 1}
                  </div>
                  <span
                    className={`text-[10px] mt-1 whitespace-nowrap ${
                      isCurrent ? "text-cyan-700 font-semibold" : isComplete ? "text-emerald-600" : "text-slate-400"
                    }`}
                  >
                    {stage.label}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </Card>

      {/* ── 2. Property Card ───────────────────────────────────────────────── */}
      <Card icon={<MapPin className="h-4 w-4 text-cyan-600" />} title="Property">
        <div className="space-y-1">
          <p className="text-sm text-slate-800 font-medium">
            {[property.streetAddress, property.unit && `Unit ${property.unit}`, property.city, property.province, property.postalCode]
              .filter(Boolean)
              .join(", ") || <span className="text-slate-400 italic">No address set</span>}
          </p>
          {property.pins?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {property.pins.map((pin: string, i: number) => (
                <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600 font-mono">
                  PIN: {pin}
                </span>
              ))}
            </div>
          )}
          {property.isCondo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium mt-1">
              <Building2 className="h-3 w-3" /> Condominium
            </span>
          )}
        </div>

        {property.legalDescription && (
          <div className="mt-3 text-xs text-slate-500">
            <span className="font-medium text-slate-600">Legal Description: </span>
            {showFullLegal || property.legalDescription.length <= 120
              ? property.legalDescription
              : `${property.legalDescription.slice(0, 120)}...`}
            {property.legalDescription.length > 120 && (
              <button
                onClick={() => setShowFullLegal(!showFullLegal)}
                className="ml-1 text-cyan-600 hover:text-cyan-700 font-medium"
              >
                {showFullLegal ? "Show less" : "Show more"}
              </button>
            )}
          </div>
        )}

        {property.isCondo && (property.condoCorpName || property.condoUnit || property.parking || property.locker) && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
            {property.condoCorpName && <InfoRow label="Condo Corp" value={property.condoCorpName} />}
            {property.condoUnit && <InfoRow label="Unit" value={property.condoUnit} />}
            {property.parking && <InfoRow label="Parking" value={property.parking} />}
            {property.locker && <InfoRow label="Locker" value={property.locker} />}
          </div>
        )}
      </Card>

      {/* ── 3. Parties Card ────────────────────────────────────────────────── */}
      <Card icon={<Users className="h-4 w-4 text-cyan-600" />} title="Parties">
        {/* Clients */}
        {parties.clients?.length > 0 ? (
          <div className="space-y-1.5 mb-3">
            {parties.clients.map((c: any, i: number) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-sm text-slate-800 font-medium">{c.name || "Unnamed"}</span>
                {c.role && (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${ROLE_COLORS[c.role] || "bg-slate-100 text-slate-600"}`}>
                    {formatLabel(c.role)}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic mb-3">No clients assigned</p>
        )}

        {/* Other side counsel */}
        {parties.otherSideCounsel?.lawyerName && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-1">Opposing Counsel</p>
            <p className="text-sm text-slate-800">{parties.otherSideCounsel.lawyerName}</p>
            {parties.otherSideCounsel.firmName && (
              <p className="text-xs text-slate-500">{parties.otherSideCounsel.firmName}</p>
            )}
            {parties.otherSideCounsel.email && (
              <a href={`mailto:${parties.otherSideCounsel.email}`} className="text-xs text-cyan-600 hover:underline">
                {parties.otherSideCounsel.email}
              </a>
            )}
          </div>
        )}

        {/* Realtor */}
        {parties.realtor?.name && (
          <div className="pt-2 mt-2 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-1">Realtor</p>
            <p className="text-sm text-slate-800">{parties.realtor.name}</p>
            {parties.realtor.brokerage && (
              <p className="text-xs text-slate-500">{parties.realtor.brokerage}</p>
            )}
          </div>
        )}

        {/* Lender */}
        {parties.lender?.name && (
          <div className="pt-2 mt-2 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-1">Lender</p>
            <p className="text-sm text-slate-800">{parties.lender.name}</p>
            {parties.lender.branch && (
              <p className="text-xs text-slate-500">Branch: {parties.lender.branch}</p>
            )}
          </div>
        )}

        {/* Title Insurer */}
        {parties.titleInsurer?.company && (
          <div className="pt-2 mt-2 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-1">Title Insurance</p>
            <p className="text-sm text-slate-800">{parties.titleInsurer.company}</p>
            {parties.titleInsurer.policyNumber && (
              <InfoRow label="Policy #" value={parties.titleInsurer.policyNumber} />
            )}
            <div className="flex items-center gap-1 mt-0.5">
              {parties.titleInsurer.ordered ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-slate-400" />
              )}
              <span className="text-xs text-slate-500">{parties.titleInsurer.ordered ? "Ordered" : "Not ordered"}</span>
            </div>
          </div>
        )}
      </Card>

      {/* ── 4. Financial Snapshot ──────────────────────────────────────────── */}
      <Card icon={<DollarSign className="h-4 w-4 text-cyan-600" />} title="Financial Snapshot">
        <div className="space-y-0.5">
          <InfoRow label="Purchase Price" value={formatCAD(financial.purchasePriceCents)} />
          <InfoRow
            label="Deposit"
            value={
              financial.depositAmountCents != null ? (
                <span>
                  {formatCAD(financial.depositAmountCents)}
                  {financial.depositHeldBy && (
                    <span className="text-slate-400 text-xs ml-1">({financial.depositHeldBy})</span>
                  )}
                </span>
              ) : (
                " - "
              )
            }
          />
          <InfoRow label="SOA Total" value={formatCAD(soa.totalDueOnClosingCents)} />

          {/* LTT */}
          {(ltt.provincialCents != null || ltt.municipalCents != null) && (
            <InfoRow
              label="Land Transfer Tax"
              value={formatCAD((ltt.provincialCents || 0) + (ltt.municipalCents || 0))}
            />
          )}

          {/* Holdbacks */}
          {financial.holdbacks?.length > 0 && (
            <InfoRow
              label={`Holdbacks (${financial.holdbacks.length})`}
              value={formatCAD(
                financial.holdbacks.reduce((sum: number, h: any) => sum + (h.amountCents || 0), 0)
              )}
            />
          )}

          {/* Trust balance */}
          {trustBalance !== null && (
            <div className="pt-2 mt-2 border-t border-slate-100">
              <InfoRow label="Trust Balance" value={formatCAD(trustBalance)} />
            </div>
          )}
        </div>
      </Card>

      {/* ── 5. Compliance Card ─────────────────────────────────────────────── */}
      <Card icon={<Shield className="h-4 w-4 text-cyan-600" />} title="Compliance">
        <div className="space-y-3">
          {/* KYC Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">KYC Verification</span>
            {(() => {
              const kycStatus = compliance.kycStatus || "not_started";
              const cfg = KYC_COLORS[kycStatus] || KYC_COLORS.not_started;
              return (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg}`}>
                  {cfg.icon} {cfg.label}
                </span>
              );
            })()}
          </div>

          {/* Conflict Check */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Conflict Check</span>
            {(() => {
              const conflictStatus = compliance.conflictCheckStatus || "not_started";
              const cfg = KYC_COLORS[conflictStatus] || KYC_COLORS.not_started;
              return (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg}`}>
                  {cfg.icon} {cfg.label}
                </span>
              );
            })()}
          </div>

          {/* Retainer Signed */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Retainer Signed</span>
            {compliance.retainerSigned ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" /> Yes
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-slate-400 text-sm">
                <XCircle className="h-4 w-4" /> No
              </span>
            )}
          </div>

          {/* Trust Classification */}
          {compliance.trustClassification && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Trust Classification</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                {formatLabel(compliance.trustClassification)}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* ── 6. Key Dates Timeline (full width) ────────────────────────────── */}
      <Card
        icon={<Calendar className="h-4 w-4 text-cyan-600" />}
        title="Key Dates"
        className="lg:col-span-2"
      >
        <div className="flex items-start justify-between gap-2 overflow-x-auto pb-2">
          <TimelineDate label="APS Date" date={dates.apsDate || null} />
          <div className="flex-shrink-0 self-center mt-[-12px]">
            <ChevronRight className="h-4 w-4 text-slate-300" />
          </div>
          <TimelineDate label="Requisition" date={dates.requisitionDate || null} />
          <div className="flex-shrink-0 self-center mt-[-12px]">
            <ChevronRight className="h-4 w-4 text-slate-300" />
          </div>
          <TimelineDate label="Signing" date={dates.signingDate || null} />
          <div className="flex-shrink-0 self-center mt-[-12px]">
            <ChevronRight className="h-4 w-4 text-slate-300" />
          </div>
          <TimelineDate label="Closing" date={dates.closingDate || null} />
        </div>
      </Card>
    </div>
  );
};

export default RealEstateOverviewTab;
